/**
 * Acquisition thresholds — minimum Listen/Drill exposures required per node
 * before the FSRS Practice gate opens in the session runner.
 *
 * One exposure = one item play in Listen or (ungraded) Drill mode.
 * Thresholds are conservative lower bounds from the perceptual learning
 * literature; harder skill clusters (inversions, modes, dictation) require
 * more massed exposure before category recognition stabilises.
 *
 * See docs/design/acquisition-gate-v5.md §1.3 for rationale.
 */
export const ACQUISITION_THRESHOLDS: Readonly<Record<string, number>> = {
  "diatonic-degrees-major": 20,
  "diatonic-degrees-minor": 20,
  "chromatic-degrees-major": 25,
  "diatonic-intervals-major": 25,
  "chromatic-intervals": 30,
  "triad-quality": 30,
  "triad-inversions": 50,
  "seventh-chord-quality": 40,
  "cadence-recognition": 30,
  "diatonic-progressions": 40,
  "secondary-dominant-recognition": 40,
  "extended-chords-9": 40,
  "extended-chords-11-13": 40,
  "altered-dominants": 40,
  "slash-chords": 40,
  "modulation-recognition": 40,
  "modes-major-family": 50,
  "modes-minor-family": 50,
  "modes-locrian": 50,
  "melodic-dictation-narrow": 60,
  "melodic-dictation-medium": 80,
  "melodic-dictation-chromatic": 100,
  // Guitar track: hearing the interval/chord is mastered upstream; the new load
  // is projecting it onto the neck, so these sit near their ear-training peers.
  "fret-octave-shapes": 20,
  "fret-scale-degrees-major": 20,
  "fret-scale-degrees-minor": 20,
  "fret-intervals-standard": 30,
  "fret-intervals-gb-offset": 30,
  "fret-triad-shapes": 30,
  "fret-triad-inversions": 50,
  "fret-caged-shapes-ea": 30,
  "fret-caged-shapes-cgd": 30,
  "fret-caged-anchoring": 30,
  "fret-scale-degrees-caged": 30,
  "fret-diatonic-triads": 30,
  "fret-diatonic-triads-minor": 30,
  "fret-seventh-shapes": 30,
};

/** Default threshold for any node not listed above. */
export const DEFAULT_ACQUISITION_THRESHOLD = 20;

/** True when the user has met the exposure threshold for this node. */
export function acquisitionMet(nodeId: string, exposures: number): boolean {
  return exposures >= (ACQUISITION_THRESHOLDS[nodeId] ?? DEFAULT_ACQUISITION_THRESHOLD);
}
